<template>
  <div class="panel-store-settings">
    <div class="panel-toolbar">
      <p class="panel-toolbar-meta">Reglas comerciales globales del canal B2B</p>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
    </div>

    <form v-else @submit.prevent="save" class="settings-grid">
      <div class="settings-card panel-table-wrap p-4">
        <h3><i class="fas fa-shopping-basket me-2"></i>Compras</h3>
        <div class="row g-3 mt-1">
          <div class="col-md-6">
            <label class="form-label">Descuento mayorista global (%)</label>
            <input v-model.number="form.distributor_discount_percentage" type="number" min="0" max="100" step="0.01" class="form-control" />
            <small class="text-muted">Porcentaje adicional para clientes mayoristas.</small>
          </div>
          <div class="col-md-6">
            <label class="form-label d-block">Mínimos por rol activos</label>
            <div v-if="activeRoleMins.length" class="role-min-badges mb-2">
              <span
                v-for="role in activeRoleMins"
                :key="'badge-' + role.id"
                class="role-min-badge"
              >
                <span class="role-min-dot" :style="{ backgroundColor: role.color || '#adb5bd' }"></span>
                {{ role.label }} · {{ formatRoleMin(role) }}
              </span>
            </div>
            <small v-else class="text-muted d-block mb-2">No hay mínimos personalizados por rol.</small>
            <button type="button" class="btn btn-link btn-sm min-config-link px-0" @click="showMinConfig = true">
              <i class="fas fa-sliders-h me-1"></i>Configurar precios de compra mínima
            </button>
          </div>
        </div>
      </div>

      <div class="settings-card panel-table-wrap p-4">
        <h3><i class="fas fa-store me-2"></i>Contacto de la tienda</h3>
        <div class="row g-3 mt-1">
          <div class="col-md-6">
            <label class="form-label">Email</label>
            <input v-model="form.email" type="email" class="form-control" />
          </div>
          <div class="col-md-6">
            <label class="form-label">Teléfono</label>
            <input v-model="form.phone" type="text" class="form-control" />
          </div>
          <div class="col-12">
            <label class="form-label">Dirección</label>
            <input v-model="form.store_address" type="text" class="form-control" />
          </div>
          <div class="col-12">
            <label class="form-label">Instagram</label>
            <input v-model="form.instagram" type="text" class="form-control" placeholder="https://instagram.com/..." />
          </div>
          <div class="col-12">
            <label class="form-label">Twitter / X</label>
            <input v-model="form.twitter" type="text" class="form-control" placeholder="https://x.com/..." />
          </div>
        </div>
      </div>

      <div class="settings-card panel-table-wrap p-4">
        <h3><i class="fas fa-user-plus me-2"></i>Registro</h3>
        <div class="form-check form-switch mt-3">
          <input
            id="access-requests-enabled"
            v-model="form.access_requests_enabled"
            class="form-check-input"
            type="checkbox"
          />
          <label class="form-check-label" for="access-requests-enabled">
            Habilitar solicitudes de acceso
          </label>
          <small class="text-muted d-block mt-1">
            Si está desactivado, la opción no aparecerá en el registro.
          </small>
        </div>
      </div>

      <div class="settings-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          <span v-if="saving"><i class="fas fa-spinner fa-spin me-1"></i>Guardando…</span>
          <span v-else><i class="fas fa-save me-1"></i>Guardar configuración</span>
        </button>
      </div>
    </form>

    <div v-if="showMinConfig" class="panel-modal-overlay" @mousedown.self="showMinConfig = false">
      <div class="panel-modal">
        <h3><i class="fas fa-shopping-basket me-2"></i>Configuración de compra mínima</h3>
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label">Compra mínima general ($)</label>
            <input v-model.number="form.min_purchase_amount" type="number" min="0" step="0.01" class="form-control" />
            <small class="text-muted">0 = sin mínimo. Se valida en el carrito.</small>
          </div>
        </div>
        <hr class="settings-divider" />
        <div class="role-mins-header">
          <h4><i class="fas fa-users me-2"></i>Compra mínima por rol</h4>
          <small class="text-muted">Usá la ruedita para editar. 0 = usar la general.</small>
        </div>
        <div v-if="rolesLoading" class="text-center py-3">
          <div class="spinner-border spinner-border-sm text-primary"></div>
        </div>
        <template v-else>
          <div v-if="roleMins.length" class="role-mins-list">
            <div v-for="role in roleMins" :key="role.id" class="role-min-row">
              <span class="role-min-dot" :style="{ backgroundColor: role.color || '#adb5bd' }"></span>
              <span class="role-min-name" :title="role.label">{{ role.label }}</span>
              <span v-if="role.min_purchase_amount > 0" class="role-min-value">{{ formatRoleMin(role) }}</span>
              <span v-else class="role-min-value role-min-general">General</span>
              <button
                type="button"
                class="btn btn-sm btn-outline-light role-min-edit"
                :aria-label="'Editar compra mínima de ' + role.label"
                @click="openRoleMinModal(role)"
              >
                <i class="fas fa-cog"></i>
              </button>
            </div>
          </div>
          <p v-else class="text-muted mb-0">No hay roles activos.</p>
        </template>
        <div class="panel-modal-actions">
          <button type="button" class="btn btn-secondary" @click="showMinConfig = false">Cerrar</button>
        </div>
      </div>
    </div>

    <div v-if="showRoleMinModal" class="panel-modal-overlay" @mousedown.self="showRoleMinModal = false">
      <div class="panel-modal panel-modal-sm">
        <h3><i class="fas fa-users me-2"></i>Editar compra mínima por rol</h3>
        <p class="role-min-modal-sub">
          <span class="role-min-dot" :style="{ backgroundColor: editingRole?.color || '#adb5bd' }"></span>
          {{ editingRole?.label }}
        </p>
        <form @submit.prevent="saveRoleMin">
          <div>
            <label class="form-label">Monto mínimo de compra ($)</label>
            <input
              v-model.number="editRoleMinValue"
              type="number"
              min="0"
              step="0.01"
              class="form-control"
              :class="{ 'is-invalid': editRoleMinInvalid }"
              autofocus
            />
            <small class="text-muted">0 = usar la compra mínima general.</small>
            <div v-if="editRoleMinInvalid" class="text-danger">No puede ser negativo.</div>
          </div>
          <div class="panel-modal-actions">
            <button type="button" class="btn btn-secondary" @click="showRoleMinModal = false">Cancelar</button>
            <button type="submit" class="btn btn-primary">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { getSiteConfig, updateSiteConfig } from '@/services/siteConfig'
import { getCustomerRoles } from '@/services/invitations'
import { getRoleMinPurchases, updateRoleMinPurchase } from '@/services/roleMinPurchase'
import { useUnsavedChangesGuard, useFormDirty } from '@/composables/useUnsavedChangesGuard'

const toast = useToast()
const loading = ref(true)
const saving = ref(false)
const rolesLoading = ref(false)
const roleMins = ref([])

// Modal principal de configuración de compra mínima (general + por rol).
const showMinConfig = ref(false)

// Estado del modal de edición de compra mínima por rol.
const showRoleMinModal = ref(false)
const editingRole = ref(null)
const editRoleMinValue = ref(0)
const editRoleMinInvalid = ref(false)

// Valores originales de los mínimos por rol para detectar cambios al guardar.
const savedRoleMins = {}

const form = ref({
  min_purchase_amount: 0,
  distributor_discount_percentage: 0,
  email: '',
  phone: '',
  store_address: '',
  instagram: '',
  twitter: '',
  access_requests_enabled: true,
})

const formDirty = useFormDirty(form)
const roleMinOriginal = ref(0)

const normalizeMinimum = (value) => {
  if (value === '' || value === null || value === undefined || Number.isNaN(Number(value))) {
    return 0
  }
  return Number(value)
}

// Cambios sin guardar: form general, valor tipeado en el modal de mínimos
// por rol, o mínimos modificados que todavía no se persistieron.
const dirty = computed(() =>
  formDirty.dirty.value ||
  (showRoleMinModal.value && editRoleMinValue.value !== roleMinOriginal.value) ||
  roleMins.value.some((r) => Number(r.min_purchase_amount ?? 0) !== Number(savedRoleMins[r.id]))
)

const savePendingChanges = async () => {
  if (showRoleMinModal.value) saveRoleMin()
  await save()
}

useUnsavedChangesGuard({
  dirty,
  saveFn: savePendingChanges,
  message: 'Tenés cambios sin guardar en la configuración. Si salís ahora, se van a perder.',
})

const loadRoleMins = async () => {
  rolesLoading.value = true
  try {
    const [roles, minPurchases] = await Promise.all([
      getCustomerRoles({ active_only: 'true' }),
      getRoleMinPurchases(),
    ])
    const rolesArr = Array.isArray(roles) ? roles : (roles.results || [])
    const minsArr = Array.isArray(minPurchases) ? minPurchases : (minPurchases.results || [])
    const minsByRole = new Map(minsArr.map((m) => [m.id, m.min_purchase_amount]))

    roleMins.value = rolesArr
      .filter((r) => r.is_active !== false)
      .map((r) => {
        const value = Number(minsByRole.get(r.id) ?? r.min_purchase_amount ?? 0)
        const item = { id: r.id, slug: r.slug, label: r.label, color: r.color, min_purchase_amount: value }
        savedRoleMins[item.id] = value
        return item
      })
  } catch {
    toast.error('No se pudieron cargar los mínimos por rol')
  } finally {
    rolesLoading.value = false
  }
}

const load = async () => {
  loading.value = true
  try {
    const data = await getSiteConfig()
    form.value = {
      min_purchase_amount: Number(data.min_purchase_amount) || 0,
      distributor_discount_percentage: Number(data.distributor_discount_percentage) || 0,
      email: data.email || '',
      phone: data.phone || '',
      store_address: data.store_address || '',
      instagram: data.instagram || '',
      twitter: data.twitter || '',
      access_requests_enabled: data.access_requests_enabled !== false,
    }
    await loadRoleMins()
    formDirty.markClean()
  } catch {
    toast.error('No se pudo cargar la configuración')
  } finally {
    loading.value = false
  }
}

const validateRoleMins = () =>
  roleMins.value.every((role) => {
    const v = Number(role.min_purchase_amount ?? 0)
    return typeof v === 'number' && !Number.isNaN(v) && v >= 0
  })

const activeRoleMins = computed(() =>
  roleMins.value.filter((role) => Number(role.min_purchase_amount ?? 0) > 0)
)

const formatRoleMin = (role) =>
  '$' + (Number(role.min_purchase_amount) || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

const openRoleMinModal = (role) => {
  editingRole.value = role
  roleMinOriginal.value = Number(role.min_purchase_amount) || 0
  editRoleMinValue.value = Number(role.min_purchase_amount) || 0
  editRoleMinInvalid.value = false
  showRoleMinModal.value = true
}

const saveRoleMin = () => {
  if (typeof editRoleMinValue.value === 'number' && editRoleMinValue.value < 0) {
    editRoleMinInvalid.value = true
    return
  }
  if (editingRole.value) {
    editingRole.value.min_purchase_amount = normalizeMinimum(editRoleMinValue.value)
  }
  showRoleMinModal.value = false
}

const save = async () => {
  if (!validateRoleMins()) {
    toast.error('Los mínimos por rol no pueden ser negativos')
    return
  }
  saving.value = true
  try {
    await updateSiteConfig({
      min_purchase_amount: normalizeMinimum(form.value.min_purchase_amount),
      email: form.value.email,
      phone: form.value.phone,
      store_address: form.value.store_address,
      instagram: form.value.instagram,
      twitter: form.value.twitter,
      distributor_discount_percentage: form.value.distributor_discount_percentage,
      access_requests_enabled: form.value.access_requests_enabled,
    })

    const changed = roleMins.value.filter(
      (role) => Number(role.min_purchase_amount ?? 0) !== Number(savedRoleMins[role.id])
    )
    for (const role of changed) {
      await updateRoleMinPurchase(role.id, role.min_purchase_amount ?? 0)
      savedRoleMins[role.id] = Number(role.min_purchase_amount ?? 0)
    }

    toast.success('Configuración guardada')
    formDirty.markClean()
  } catch (e) {
    const msg = e.response?.data?.error || e.response?.data?.detail || 'Error al guardar'
    toast.error(msg)
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.settings-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.settings-card h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--white-color);
  margin: 0;
}

.settings-actions {
  display: flex;
  justify-content: flex-end;
}

.panel-store-settings .form-control {
  background: rgba(0, 0, 0, 0.25);
  border-color: var(--panel-border);
  color: var(--white-color);
}

.panel-store-settings .form-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
}

.settings-divider {
  border-color: var(--panel-border);
  margin: 1rem 0;
}

.role-mins-header {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin-bottom: 0.75rem;
}

.role-mins-header h4 {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--white-color);
  margin: 0;
}

.role-mins-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.role-min-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.75rem;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid var(--panel-border);
  border-radius: 10px;
}

.role-min-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.role-min-name {
  flex: 1;
  min-width: 0;
  font-size: 0.9rem;
  color: var(--white-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role-min-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary, #7f2bc3);
}

.role-min-general {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 500;
  font-style: italic;
}

.role-min-modal-sub {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: -0.5rem 0 1rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.75);
}

.role-min-edit {
  padding: 0.2rem 0.55rem;
  font-size: 0.75rem;
}

.min-config-link {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
}

.min-config-link:hover {
  text-decoration: underline;
}

.role-min-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.role-min-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  border: 1px solid var(--panel-border);
  background: rgba(0, 0, 0, 0.24);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.75rem;
  font-weight: 600;
}

.panel-modal-sm {
  max-width: 420px;
}

.panel-store-settings .form-control.is-invalid {
  border-color: #ff5a5a;
}
</style>
